import { CdkScrollable } from '@angular/cdk/scrolling'
import { CommonModule } from '@angular/common'
import { Component, computed, DestroyRef, effect, Inject, inject, input, OnInit, Signal } from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { Data, RouterModule } from '@angular/router'
import { AppMode } from '@ftr/contracts/api/user'
import { LayoutService, ScreenSize, ScreenSizeService } from '@ftr/foundation'
import { APP_MODE } from '@ftr/routing-paths'
import {
  CourtsSideNavDisplayComponent,
  CourtsSideNavItemsProvider,
  PublicSideNavDisplayComponent,
  PublicSideNavItemsProvider,
  SideNavigationMenuToggledEvent,
  SideNavigationMenuTogglingEvent,
  SideNavProviderResult,
} from '@ftr/ui-core'
import { featureFlag } from '@ftr/ui-feature-flags'
import { RequestTypeUrlValue } from '@ftr/ui-request'
import { UserState } from '@ftr/ui-user'
import { Store } from '@ngxs/store'
import { map, Observable } from 'rxjs'
import { TopBarDisplayComponent } from '../top-bar-display'

const DEBOUNCE_WINDOW_RESIZE_MS = 50

@Component({
  selector: 'ftr-app-layout',
  templateUrl: './app-layout.component.html',
  styleUrls: ['./app-layout.component.css'],
  imports: [
    CommonModule,
    RouterModule,
    CourtsSideNavDisplayComponent,
    PublicSideNavDisplayComponent,
    TopBarDisplayComponent,
    CdkScrollable,
  ],
})
export class AppLayoutComponent implements OnInit {
  routeData = input({} as Data | undefined)
  showSearch = input<boolean>(true)

  // Controls whether the side nav is opened or closed (only for small screenss)
  protected sideNavOpen: boolean = false
  // Controls whether the side nav is expanded or not (only for large screens)
  protected sideNavExpanded: boolean = false
  protected hasToggledSideNav: boolean = false

  private isPublicNavigationEnabled = featureFlag('public-navigation')

  private currentCourtSystemIdFromStore = toSignal(
    this.store.select(UserState.currentCourtSystem).pipe(map(currentCourtSystem => currentCourtSystem?.id)),
  )

  protected currentCourtSystemId: Signal<string | undefined> = computed(() =>
    this.isPublicNavigationEnabled() ? this.currentCourtSystemIdFromStore() : this.routeData()?.courtSystemId,
  )

  protected currentRequestType = computed<RequestTypeUrlValue | undefined>(() => {
    return this.routeData()?.requestType
  })

  protected sideNavMode = computed(() => {
    const currentSize = this.screenSizeService.sizeSignal()
    if (currentSize < ScreenSize.Large) {
      return 'OVERLAY'
    }
    return 'DEFAULT'
  })

  protected sideNavModel: Observable<SideNavProviderResult>
  protected readonly appContentWindowSelector = LayoutService.CONTENT_WINDOW_CLASS
  protected readonly appContentWindowSelectorGreyBackground = `${this.appContentWindowSelector}--grey-background`

  readonly #destroyRef = inject(DestroyRef)

  constructor(
    private screenSizeService: ScreenSizeService,
    private courtsSideNavItemsProvider: CourtsSideNavItemsProvider,
    private publicSideNavItemsProvider: PublicSideNavItemsProvider,
    private store: Store,
    @Inject(APP_MODE) protected readonly appMode: Signal<AppMode>,
  ) {
    effect(() => {
      const courtSystemId = this.currentCourtSystemId()
      if (this.appMode() === 'public') {
        this.sideNavModel = this.publicSideNavItemsProvider.provide()
      } else {
        this.sideNavModel = this.courtsSideNavItemsProvider.provide(courtSystemId)
      }
    })
  }

  ngOnInit(): void {
    this.watchScreenSize()
  }

  handleSideNavExpandedChange(): void {
    this.store.dispatch(new SideNavigationMenuTogglingEvent())
    this.sideNavExpanded = !this.sideNavExpanded
    this.hasToggledSideNav = true
    this.store.dispatch(new SideNavigationMenuToggledEvent())

    // Fake a window resize event because while the window hasn't changed, the content panel has
    setTimeout(() => window.dispatchEvent(new Event('resize')), DEBOUNCE_WINDOW_RESIZE_MS)
  }

  handleSideNavOpenChange(forceState?: boolean): void {
    this.sideNavOpen = forceState !== undefined ? forceState : !this.sideNavOpen
  }

  private watchScreenSize(): void {
    this.screenSizeService.size.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(size => {
      if (!this.hasToggledSideNav) {
        if (size >= ScreenSize.XLarge) {
          this.sideNavExpanded = true
        }

        if (size === ScreenSize.Large) {
          this.sideNavExpanded = false
        }
      }

      if (this.sideNavOpen && size > ScreenSize.Medium) {
        this.sideNavOpen = false
      }
    })
  }
}
